import { useEffect, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type { TargetKind } from '../../lib/targetKind';

/**
 * Edit a target's metadata from its drawer — the four things that used to need a
 * hand-written `updateAtlasProject` call in Apollo Sandbox: display name, description,
 * the active flag, and rotating the stored secret.
 *
 * NO NEW BACKEND. `UpdateAtlasProjectInput` already carries all four, and the api is
 * still the enforcement (`@RequireRole('root','maintainer')`) — the role test below
 * only decides whether a control that would 403 is drawn at all.
 *
 * WHAT IS DELIBERATELY NOT HERE: `projectId`, `clientId`, `doTeam`, `isDefault`, and
 * anything that would change a target's KIND. The input has no `kind` field on purpose
 * (a target cannot be flipped between products underneath its credentials), and
 * repointing a live target at another database is a different, riskier action than
 * fixing its name.
 *
 * THE SECRET IS WRITE-ONLY. The api never returns it — `AtlasProjectGql` carries
 * `hasCredentials`, a boolean, and nothing else about it — so this form has nothing to
 * hide and one rule to keep: never put a credential in a value, a placeholder, a title
 * or a data attribute. The rotate field starts empty and is cleared on success; empty
 * means "keep the stored one" because the key is then omitted from the mutation
 * entirely (service `:594`).
 */

/** The one query that carries `description` — the overview does not select it. */
const TARGET_DETAIL = gql`
  query AtlasTargetDetail($projectKey: String!) {
    atlasProject(projectKey: $projectKey) {
      projectKey
      displayName
      description
      isActive
      hasCredentials
      kind
    }
  }
`;

const UPDATE_TARGET = gql`
  mutation UpdateAtlasProject($input: UpdateAtlasProjectInput!) {
    updateAtlasProject(input: $input) {
      projectKey
      displayName
      description
      isActive
      hasCredentials
    }
  }
`;

interface TargetDetail {
  projectKey: string;
  displayName: string | null;
  description: string | null;
  isActive: boolean;
  hasCredentials: boolean;
  kind: string;
}

export function TargetEditForm({
  projectKey,
  kind,
  onClose,
  onSaved,
  onBusyChange,
}: {
  projectKey: string;
  kind: TargetKind;
  onClose: () => void;
  /** The dialog this renders inside refuses Escape and Cancel while a save is in
   *  flight (owner decision e-22 put the form in a modal); the flight is known here,
   *  so it is reported up rather than duplicated there. */
  onBusyChange?: (busy: boolean) => void;
  /** The drawer title, the capacity bar and the band row behind the drawer all read
   *  `atlasProjectsOverview` — refetched here so the new name lands everywhere with
   *  no reload (AC-7). */
  onSaved: () => unknown;
}) {
  const detail = useQuery<{ atlasProject: TargetDetail }>(TARGET_DETAIL, {
    variables: { projectKey },
    fetchPolicy: 'cache-and-network',
  });

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [secret, setSecret] = useState('');
  // Prefill once the row arrives, and only then: re-seeding on every render would
  // fight the typist, and seeding from `undefined` would blank a field that has a value.
  const loaded = detail.data?.atlasProject;
  // F-4 (review e-11): ONCE PER TARGET, not once per `loaded` identity. `loaded` is a
  // new object on every `cache-and-network` result, and this form's own `save()` ends
  // with `detail.refetch()` — so on the refetch of a slow save the effect re-ran and
  // overwrote whatever the operator had typed since. Keying on the projectKey the seed
  // came from also makes the drawer switching targets do the right thing: a different
  // key seeds again, the same key never does.
  const seededFor = useRef<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!loaded || seededFor.current === loaded.projectKey) return;
    seededFor.current = loaded.projectKey;
    setDisplayName(loaded.displayName ?? '');
    setDescription(loaded.description ?? '');
    setIsActive(loaded.isActive);
    // The fields do not exist until the row arrives, so `Dialog`'s own open-time
    // focus lands on the first button instead — the same reason the Add wizard
    // re-focuses on a step change. Nominate Display name once it is there.
    queueMicrotask(() => bodyRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus());
  }, [loaded]);

  const [update, state] = useMutation<{ updateAtlasProject: TargetDetail }>(UPDATE_TARGET);

  useEffect(() => {
    onBusyChange?.(state.loading);
  }, [state.loading, onBusyChange]);

  const isDo = kind === 'do_db';
  const secretLabel = isDo ? 'Rotate DO API token' : 'Rotate OAuth client secret';

  const save = async () => {
    // `''` and `null` are DIFFERENT instructions to the service: it reads null as
    // "keep what is stored" (`:596-597`), so a cleared field must travel as an empty
    // string or the clear silently does nothing.
    const input: Record<string, unknown> = {
      projectKey,
      displayName: displayName.trim(),
      description: description.trim(),
      isActive,
    };
    // Omitted, not null: the key's ABSENCE is what keeps the stored secret, and
    // sending `clientSecret: null` would be indistinguishable from a rotate that lost
    // its value on the way. AC-7 asserts the key is not on the wire.
    // F-5 (review e-11): the TRIMMED value on the wire. A token pasted from a
    // terminal or a password manager carries a newline often enough, and an
    // untrimmed one is stored encrypted — the failure then surfaces as a 401 from
    // the vendor, days later, with nothing on screen that made it.
    if (secret.trim()) input.clientSecret = secret.trim();
    const res = await update({ variables: { input } }).catch(() => null);
    if (!res || res.error || !res.data) return; // stays open, error rendered below
    setSecret('');
    await Promise.resolve(onSaved()).catch(() => undefined);
    await detail.refetch().catch(() => undefined);
    onClose();
  };

  const blocked = (detail.loading && !loaded) || Boolean(detail.error);

  return (
    /* The heading lives on the Dialog (its accessible name), so there is none here —
       two "Edit target" lines, one of them the dialog's own title, is one too many.
       Body and actions are SIBLINGS, not nested: `.dialog-body` scrolls at 62vh, and
       a Save button inside it scrolls out of reach on a short window. */
    <>
      <div className="dialog-body atlas-edit" data-testid="target-edit" ref={bodyRef}>
      {detail.loading && !loaded ? (
        <p className="muted">Loading…</p>
      ) : detail.error ? (
        <p className="error">Couldn&apos;t load this target — {detail.error.message}</p>
      ) : (
        <>
          <label style={{ display: 'block', marginBottom: '0.6rem' }}>
            <div className="kv-k" style={{ marginBottom: 2 }}>
              Display name
            </div>
            <input
              className="class-select"
              type="text"
              data-autofocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={{ width: '100%', maxWidth: 460 }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: '0.6rem' }}>
            <div className="kv-k" style={{ marginBottom: 2 }}>
              Description
            </div>
            <textarea
              className="class-select"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', maxWidth: 460 }}
            />
          </label>

          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: '0.6rem' }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
          <div className="muted" style={{ fontSize: '0.8rem', marginBottom: '0.6rem' }}>
            An inactive target is skipped by every reconcile and disappears from the cluster
            mapping dropdowns — the mapping rows themselves are kept.
          </div>

          <label style={{ display: 'block', marginBottom: '0.6rem' }}>
            <div className="kv-k" style={{ marginBottom: 2 }}>
              {secretLabel}
            </div>
            <input
              className="class-select"
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              style={{ width: '100%', maxWidth: 460 }}
            />
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
              Leave blank to keep the stored one. The stored value is never shown — this app
              only ever knows whether one exists.
            </div>
          </label>

          {state.error && <p className="error">{state.error.message}</p>}

        </>
      )}
      </div>

      {/* Cancel first, primary last — the same order as the Add wizard's footer, so
          the two credential forms are not mirror images of each other. */}
      <div className="dialog-actions">
        <button type="button" className="btn" disabled={state.loading} onClick={onClose}>
          Cancel
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary"
          disabled={state.loading || blocked}
          onClick={() => void save()}
        >
          {state.loading ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}
